import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid, Cell,
    Label,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from "recharts";

import {Tooltip as TooltipChart} from "recharts";
import {recordsLimit} from "../../constants";
import {useAQM} from "../../context";

import {AvgSvg} from "../AvgSvg";
import {BarShape} from "../BarShape";
import {MaxSvg} from "../MaxSvg";
import {MinSvg} from "../MinSvg";
import {Column} from "./components/Column";
import {topRow} from "./components/styles";
import {plotContent} from "./styles";
import { styled } from '@mui/material/styles';

import InfoIcon from '@mui/icons-material/Info';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';


const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 291,
    },
});

const fakeData = Array(recordsLimit).fill(0).map(x => ({
    name: "2020/05/14",
    value: Math.random() * 400,
}));


const formatNumber = (n: number, digits: number) => {
    return parseFloat(n.toFixed(digits));
}

export const ConcentrationPlot: React.FC = () => {

    const {measurements: {min, max, avg}, data} = useAQM();


    const barData = React.useMemo(() => {
        // return fakeData; // Uncomment this line to quickly test fake data
        if (data) {
            return data.map(x => ({
                name: x.exposureOn?.replaceAll('-', '/'),
                value: x.concentration,
            }))
        }
        return [];
    }, [data]);

    const dataMax = React.useMemo(() => {
        const t = barData.map(x => x.value).filter((x): x is number => typeof x === "number");
        t.push(150);
        return Math.ceil(Math.max.apply(null, t));
    }, [barData]);

    const tooltipText = `
    National Ambiant Air Quality Standards (40 CFR Part 50)
    Particulate Matters 10 Microns or Smaller (PM10):    
    Not to exceed 150 µg/m³ more than once per year on average over 3 years
    `;

    return (
        <React.Fragment>
            <div css={topRow}>
                <Column variant={"blue"} name={"Average"} value={formatNumber(avg, 2)} icon={<AvgSvg/>}/>
                <Column variant={"lightblue"} name={"Max"} value={formatNumber(max, 2)} icon={<MaxSvg/>}/>
                <Column variant={"yellow"} name={"Min"} value={formatNumber(min, 2)} icon={<MinSvg/>}/>
            </div>

            <div css={plotContent}>
                <div style={{position:'absolute',left:'59.5%',top:'6.2%', zIndex:1}}>
                    <CustomWidthTooltip title={tooltipText} placement="top" arrow>
                        <IconButton>
                            <InfoIcon width={25} height={25} />
                        </IconButton>
                    </CustomWidthTooltip>
                </div>
                <ResponsiveContainer height={400}>
                    <BarChart data={barData} margin={{
                        top: 30, bottom: 50,
                    }}>
                        <CartesianGrid vertical={false} strokeDasharray="4 4"/>
                        <XAxis stroke={"#CCCCCC"} textAnchor="end"
                               tickLine={false} type="category" dataKey="name" angle={-45}/>
                        <YAxis stroke={"#CCCCCC"} domain={[0, dataMax]} tickCount={15}
                               label={{
                                   value: 'Concentration',
                                   angle: -90,
                                   position: 'insideLeft',
                                   style: {fill: "lightgrey"},
                               }}
                               tickLine={false} axisLine={false} type="number">
                        </YAxis>
                        <TooltipChart/>


                        <defs>
                            <linearGradient id="Gradient1">
                                <stop className="stop1" offset="0%"/>
                                <stop className="stop2" offset="50%"/>
                                <stop className="stop3" offset="100%"/>
                            </linearGradient>
                            <linearGradient id="Gradient2" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="red"/>
                                <stop offset="50%" stopColor="black" stopOpacity="0"/>
                                <stop offset="100%" stopColor="blue"/>
                            </linearGradient>
                        </defs>

                        <Bar
                            dataKey="value"
                            fill="#449DF7"
                            barSize={32}
                            // @ts-ignore
                            shape={BarShape}
                            // @ts-ignore
                            radius={[200, 200, 0, 0] as unknown as number}
                        />

                        <ReferenceLine
                            y={150}
                            // @ts-ignore
                            label={

                                    <Label
                                        value={"NAAQS 150 µg/m³"}
                                        position={"top"}
                                    >
                                    </Label>

                            }
                            stroke="red" strokeDasharray="3 3"
                        />

                    </BarChart>
                </ResponsiveContainer>
            </div>
        </React.Fragment>
    );
};
